import apiCall from './api';

/**
 * Service amélioré pour la gestion des clients
 * Inclut la gestion des crédits et statistiques avancées
 */
export const customerService = {
  
  /**
   * ✅ Récupérer tous les clients de la boutique
   */
  getCustomers: async (params = {}) => {
    try {
      
      const response = await apiCall.get('/employee/customers', params);
      
      if (response.success) {
        return response;
      }
      
      return { success: false, data: [], message: 'Aucun client trouvé' };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des clients:', error);
      return { success: false, data: [], message: 'Erreur de connexion' };
    }
  },

  /**
   * ✅ NOUVEAU : Clients paginés + filtres côté serveur
   */
  getCustomersFiltered: async (page = 0, size = 20, filters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('size', size);

      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.creditStatus) params.append('creditStatus', filters.creditStatus);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

      const response = await apiCall.get(`/employee/customers/filtered?${params.toString()}`);

      if (response.success && response.data) {
        const customersData = response.data.customers || response.data;
        const content = customersData.content || [];

        const formattedCustomers = content.map(c =>
          customerService.formatCustomerForDisplay(c)
        ).filter(c => c !== null);

        return {
          success: true,
          data: formattedCustomers,
          pagination: {
            currentPage: customersData.page_number ?? customersData.pageNumber ?? 0,
            totalPages: customersData.total_pages ?? customersData.totalPages ?? 1,
            totalElements: customersData.total_elements ?? customersData.totalElements ?? 0,
            pageSize: customersData.page_size ?? customersData.pageSize ?? size,
            hasNext: customersData.has_next ?? customersData.hasNext ?? false,
            hasPrevious: customersData.has_previous ?? customersData.hasPrevious ?? false,
          },
          stats: response.data.stats || {},
        };
      }

      return { success: false, data: [], pagination: null, stats: {} };
    } catch (error) {
      console.error('❌ Erreur clients filtrés:', error);
      return { success: false, data: [], pagination: null, stats: {}, message: error.message };
    }
  },

  /**
   * ✅ Récupérer un client par ID avec ses statistiques
   */
  getCustomerById: async (customerId) => {
    try {
      
      const response = await apiCall.get(`/employee/customers/${customerId}`);
      
      if (response.success) {
        return response;
      }
      
      return { success: false, data: null, message: 'Client non trouvé' };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du client:', error);
      return { success: false, data: null, message: 'Erreur de connexion' };
    }
  },

  /**
   * ✅ Rechercher des clients
   */
  searchCustomers: async (query) => {
    try {
      
      if (!query || query.trim().length < 2) {
        return { success: true, data: [] };
      }
      
      const response = await apiCall.get('/employee/customers/search', { query });
      
      if (response.success) {
        return response;
      }
      
      return { success: false, data: [], message: 'Aucun résultat' };
    } catch (error) {
      console.error('❌ Erreur lors de la recherche:', error);
      return { success: false, data: [], message: 'Erreur de recherche' };
    }
  },

  /**
   * ✅ Créer un nouveau client
   */
  createCustomer: async (customerData) => {
    try {
      
      // Nettoyer les données avant envoi
      const cleanData = {
        name: customerData.name?.trim(),
        phone: customerData.phone?.trim() || null,
        email: customerData.email?.trim() || null,
        address: customerData.address?.trim() || null,
        // Champs revendeur
        is_reseller: customerData.isReseller || customerData.is_reseller || false,
        reseller_discount: customerData.resellerDiscount || customerData.reseller_discount || 3000
      };
      
      // Supprimer les champs vides
      Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === '' || cleanData[key] === undefined) {
          cleanData[key] = null;
        }
      });
      
      const response = await apiCall.post('/employee/customers', cleanData);
      
      if (response.success) {
        return response;
      }
      
      return { success: false, message: response.message || 'Erreur lors de la création' };
    } catch (error) {
      console.error('❌ Erreur lors de la création du client:', error);
      
      if (error.response?.status === 400) {
        return { 
          success: false, 
          message: error.response.data?.message || 'Données invalides' 
        };
      }
      
      return { success: false, message: 'Erreur de connexion' };
    }
  },

  /**
 * ✅ CORRECTION : Mettre à jour un client avec le bon endpoint
 */
updateCustomer: async (customerId, customerData) => {
  try {
    
    // Nettoyer et formater les données avant envoi
    const cleanData = {
      name: customerData.name?.trim(),
      phone: customerData.phone?.trim() || null,
      email: customerData.email?.trim() || null,
      address: customerData.address?.trim() || null,
      // Champs revendeur avec snake_case pour le backend
      is_reseller: customerData.isReseller || customerData.is_reseller || false,
      reseller_discount: customerData.resellerDiscount || customerData.reseller_discount || 3000
    };
    
    
    const response = await apiCall.put(`/employee/customers/${customerId}`, cleanData);
    
    if (response.success) {
      return response;
    }
    
    return { success: false, message: response.message || 'Erreur lors de la mise à jour' };
  } catch (error) {
    console.error('❌ Erreur lors de la mise à jour du client:', error);
    
    // ✅ Gestion d'erreur améliorée
    if (error.response?.status === 500) {
      return { 
        success: false, 
        message: 'Erreur serveur lors de la mise à jour. Vérifiez les données saisies.' 
      };
    }
    
    if (error.response?.status === 400) {
      return { 
        success: false, 
        message: error.response.data?.message || 'Données invalides' 
      };
    }
    
    return { success: false, message: 'Erreur de connexion' };
  }
},

  /**
   * ✅ Récupérer les clients VIP
   */
  getVipCustomers: async () => {
    try {
      
      const response = await apiCall.get('/manager/customers/vip');
      
      if (response.success) {
        return response;
      }
      
      return { success: false, data: [], message: 'Aucun client VIP trouvé' };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des clients VIP:', error);
      return { success: false, data: [], message: 'Erreur de connexion' };
    }
  },

  /**
   * ✅ Récupérer les clients avec crédit en cours
   */
  getCustomersWithCredit: async () => {
    try {
      
      const response = await apiCall.get('/manager/customers/credit');
      
      if (response.success) {
        return response;
      }
      
      return { success: false, data: [], message: 'Aucun client avec crédit trouvé' };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des clients avec crédit:', error);
      return { success: false, data: [], message: 'Erreur de connexion' };
    }
  },

  /**
   * ✅ Obtenir les statistiques d'un client
   */
  getCustomerStats: async (customerId) => {
    try {
      
      const response = await apiCall.get(`/employee/customers/${customerId}/stats`);
      
      if (response.success) {
        return response;
      }
      
      return { success: false, data: null, message: 'Erreur lors de la récupération des statistiques' };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des statistiques:', error);
      return { success: false, data: null, message: 'Erreur de connexion' };
    }
  },

  /**
   * ✅ Formater un client pour l'affichage
   */
  formatCustomerForDisplay: (customer) => {
    if (!customer) return null;
    
    return {
      id: customer.id,
      name: customer.name,
      phone: customer.phone,
      email: customer.email,
      address: customer.address,
      boutiqueId: customer.boutique_id || customer.boutiqueId,
      totalPurchases: customer.total_purchases || customer.totalPurchases || 0,
      purchaseCount: customer.purchase_count || customer.purchaseCount || 0,
      isVip: customer.is_vip || customer.isVip || false,
      lastPurchaseDate: customer.last_purchase_date || customer.lastPurchaseDate,
      createdAt: customer.created_at || customer.createdAt,
      
      // Champs revendeur
      isReseller: customer.is_reseller || customer.isReseller || false,
      resellerDiscount: customer.reseller_discount || customer.resellerDiscount || 3000,
      
      // Champs calculés
      displayTotalPurchases: formatAmount(customer.total_purchases || customer.totalPurchases || 0),
      loyaltyLevel: calculateLoyaltyLevel(customer.total_purchases || customer.totalPurchases || 0),
      daysSinceLastPurchase: calculateDaysSinceLastPurchase(customer.last_purchase_date || customer.lastPurchaseDate),
      formattedCreatedAt: formatDate(customer.created_at || customer.createdAt),
      formattedResellerDiscount: formatAmount(customer.reseller_discount || customer.resellerDiscount || 3000)
    };
  },

  /**
   * ✅ Valider les données d'un client
   */
  validateCustomerData: (customerData) => {
    const errors = [];
    
    if (!customerData.name || customerData.name.trim().length < 2) {
      errors.push('Le nom doit contenir au moins 2 caractères');
    }
    
    if (customerData.phone && !/^[\+]?[\d\s\-\(\)]{8,}$/.test(customerData.phone.trim())) {
      errors.push('Format de téléphone invalide');
    }
    
    if (customerData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.email.trim())) {
      errors.push('Format d\'email invalide');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * ✅ Filtrer les clients selon différents critères
   */
  filterCustomers: (customers, filters) => {
    if (!Array.isArray(customers)) return [];
    
    let filtered = [...customers];
    
    // Filtre par statut
    if (filters.status && filters.status !== 'all') {
      if (filters.status === 'vip') {
        filtered = filtered.filter(c => c.isVip);
      } else if (filters.status === 'regular') {
        filtered = filtered.filter(c => !c.isVip);
      }
    }
    
    // Filtre par crédit
    if (filters.creditStatus && filters.creditStatus !== 'all') {
      if (filters.creditStatus === 'has_credit') {
        filtered = filtered.filter(c => c.totalPurchases > 0);
      } else if (filters.creditStatus === 'no_credit') {
        filtered = filtered.filter(c => c.totalPurchases === 0);
      }
    }
    
    // Filtre par recherche
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm) ||
        (customer.phone && customer.phone.includes(searchTerm)) ||
        (customer.email && customer.email.toLowerCase().includes(searchTerm))
      );
    }
    
    return filtered;
  },

  /**
   * ✅ Trier les clients
   */
  sortCustomers: (customers, sortBy, sortDirection = 'ASC') => {
    if (!Array.isArray(customers)) return [];
    
    return customers.sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'name':
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
          break;
        case 'totalPurchases':
          valueA = a.totalPurchases || 0;
          valueB = b.totalPurchases || 0;
          break;
        case 'purchaseCount':
          valueA = a.purchaseCount || 0;
          valueB = b.purchaseCount || 0;
          break;
        case 'createdAt':
          valueA = new Date(a.createdAt);
          valueB = new Date(b.createdAt);
          break;
        default:
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
      }
      
      if (sortDirection === 'DESC') {
        return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
      } else {
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      }
    });
  },

  /**
   * ✅ Calculer les statistiques globales des clients
   */
  calculateCustomersStats: (customers) => {
    if (!Array.isArray(customers) || customers.length === 0) {
      return {
        total: 0,
        vip: 0,
        withPurchases: 0,
        totalRevenue: 0,
        averageRevenue: 0
      };
    }
    
    const stats = {
      total: customers.length,
      vip: customers.filter(c => c.isVip).length,
      withPurchases: customers.filter(c => c.totalPurchases > 0).length,
      totalRevenue: customers.reduce((sum, c) => sum + (c.totalPurchases || 0), 0),
      averageRevenue: 0
    };
    
    stats.averageRevenue = stats.withPurchases > 0 ? 
      stats.totalRevenue / stats.withPurchases : 0;
    
    return stats;
  }
};

/**
 * Fonctions utilitaires
 */

/**
 * Formater un montant en devise locale
 */
const formatAmount = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF',
    minimumFractionDigits: 0
  }).format(amount || 0);
};

/**
 * Formater une date
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Calculer le niveau de fidélité
 */
const calculateLoyaltyLevel = (totalAmount) => {
  if (!totalAmount || totalAmount === 0) return 'Nouveau';
  
  if (totalAmount >= 1000000) return 'VIP Gold';
  if (totalAmount >= 500000) return 'VIP Silver';
  if (totalAmount >= 100000) return 'Fidèle';
  if (totalAmount >= 50000) return 'Régulier';
  return 'Occasionnel';
};

/**
 * Calculer les jours depuis le dernier achat
 */
const calculateDaysSinceLastPurchase = (lastPurchaseDate) => {
  if (!lastPurchaseDate) return null;
  
  const now = new Date();
  const lastPurchase = new Date(lastPurchaseDate);
  const diffTime = Math.abs(now - lastPurchase);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

export default customerService;